import { AutoComplete, DatePicker, notification } from "antd";
import {
  Button,
  Checkbox,
  Input,
  NumberInput,
  Select,
  Textarea,
} from "@mantine/core";
import {
  IconBuilding,
  IconCalculator,
  IconCalendar,
  IconCashBanknote,
  IconChevronDown,
  IconFileText,
  IconFolderOpen,
  IconHash,
  IconMail,
  IconMessage,
  IconPercentage,
  IconPlus,
  IconReceipt,
  IconShield,
  IconShieldCheck,
  IconTicket,
  IconTrash,
  IconUsers,
  IconWallet,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single, allOrThrow } from "@/lib/orpc-batch";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

const formatDecimal = (value: number | string | undefined | null) =>
  Math.round(Number(value) || 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });

function EditAccountReceivablePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const [accountReceivableData, setAccountReceivableData] = useState({
    name: "",
    requestedAmount: 0,
    taxRate: 0,
    partnerId: "",
    remarks: "",
  });
  const [partnerData, setPartnerData] = useState([]);
  const [bankData, setBankData] = useState([]);
  const [receivableList, setReceivableList] = useState<any[]>([]);
  const [deleteReceivableList, setDeleteReceivableList] = useState<string[]>([]);
  const [tempPartnerInput, setTempPartnerInput] = useState("");
  const [options, setOptions] = useState([]);
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(true);
  const [performanceBond, setPerformanceBond] = useState({
    checkNumber: "",
    retrievalDate: null as string | null,
    isRetrieved: false,
  });
  const [engineeringInsurance, setEngineeringInsurance] = useState({
    startDate: null as string | null,
    expiryDate: null as string | null,
  });
  const [warrantyBond, setWarrantyBond] = useState({
    checkNumber: "",
    retrievalDate: null as string | null,
    isRetrieved: false,
  });

  const handleCancelButton = () => {
    navigate("/hrMainPage/accountReceivable");
  };

  const handleChangePartnerData = (value, data, dataType) => {
    let match = value.match(/ID:\s*([a-zA-Z0-9-]+)/);
    let idNumber = match ? match[1] : null;
    let selectedData = data.filter((e) => e?.id == idNumber)[0];
    if (selectedData) {
      setTempPartnerInput(selectedData.name + ` (ID: ${selectedData.id})`);
      handleChangeData(selectedData.id, dataType);
    }
  };

  const handleGetPartnerData = (partnerData, text) => {
    return partnerData
      ?.map((t) => ({
        label: t?.name + ` (ID: ${t?.id})`,
        value: t?.name + ` (ID: ${t?.id})`,
      }))
      ?.filter((item) => item.label.toLowerCase().includes(text.toLowerCase()));
  };

  const getBankById = (bankId) => {
    return bankData.find((bank) => bank.id === bankId);
  };

  const handleChangeData = (value, type) => {
    setAccountReceivableData((prevData) => ({
      ...prevData,
      [type]: value,
    }));
  };

  const handleChangeReceivableDetail = (key, value, type) => {
    setReceivableList((prev) =>
      prev.map((receivable) => {
        if (receivable.id === key || receivable.key === key) {
          return { ...receivable, [type]: value };
        }
        return receivable;
      })
    );
  };

  const {
    name = "",
    requestedAmount = 0,
    taxRate = 0,
    remarks = "",
  } = accountReceivableData || {};

  const totalAmountWithTax = useMemo(
    () =>
      (Number(requestedAmount) || 0) +
      (Number(requestedAmount) || 0) * ((Number(taxRate) || 0) / 100),
    [requestedAmount, taxRate]
  );

  const paidAmount = useMemo(() => {
    return receivableList.reduce(
      (acc, t) =>
        acc + (isNaN(parseFloat(String(t.amount))) ? 0 : parseFloat(String(t.amount))),
      0
    );
  }, [receivableList]);

  const unpaidAmount = useMemo(
    () => totalAmountWithTax - paidAmount,
    [totalAmountWithTax, paidAmount]
  );

  const totalHandlingFee = useMemo(
    () => receivableList.reduce((acc, t) => acc + (t.handlingFee || 0), 0),
    [receivableList]
  );

  const totalDeductionAmount = useMemo(
    () => receivableList.reduce((acc, t) => acc + (t.deductionAmount || 0), 0),
    [receivableList]
  );

  const handleSubmit = async () => {
    try {
      for (let i = 0; i < receivableList.length; i++) {
        const record = receivableList[i];
        const missingFields = [];

        if (!record.paymentDate)
          missingFields.push(
            t("accountReceivable.collectionDate") || "收款日期"
          );
        if (!record.amount && record.amount !== 0)
          missingFields.push(
            t("accountReceivable.collectionAmount") || "收款金額"
          );
        if (!record.bankId)
          missingFields.push(t("accountReceivable.bank") || "銀行");
        if (!record.invoiceNumber)
          missingFields.push(
            t("accountReceivable.invoiceNumber") || "發票號碼"
          );
        if (!record.invoiceDate)
          missingFields.push(
            t("accountReceivable.invoiceDate") || "發票日期"
          );

        if (missingFields.length > 0) {
          api.warning({
            message: "請填寫必填欄位",
            description: (
              <div>
                收款紀錄 #{i + 1} 缺少以下必填欄位：
                <br />
                {missingFields.join("、")}
              </div>
            ),
            placement: "topLeft",
            duration: 5,
          });
          return;
        }
      }

      const result = await single(
        orpcClient.accountsReceivable.update([
          {
            id: id!,
            name: accountReceivableData.name,
            requestedAmount: Math.round(
              Number(accountReceivableData.requestedAmount) || 0
            ),
            taxRate: Math.round(Number(accountReceivableData.taxRate) || 0),
            partnerId: accountReceivableData.partnerId,
            remarks: accountReceivableData.remarks || undefined,
            documentRecords: {
              performanceBond,
              engineeringInsurance,
              warrantyBond,
            },
          },
        ])
      );

      if (result) {
        const updateList: any[] = [];
        const newList: any[] = [];

        receivableList.forEach((r) => {
          if (r?.id && !deleteReceivableList.includes(r.id)) {
            updateList.push(r);
          } else if (!r?.id) {
            newList.push(r);
          }
        });

        if (deleteReceivableList.length) {
          await allOrThrow(
            orpcClient.accountsReceivableDetail.delete(
              deleteReceivableList.map((d) => ({ id: d }))
            )
          );
        }

        if (updateList.length) {
          await allOrThrow(
            orpcClient.accountsReceivableDetail.update(
              updateList.map((data) => ({
                id: data?.id,
                accountsReceivableId: id!,
                bankId: data?.bankId,
                paymentDate: data?.paymentDate
                  ? new Date(data.paymentDate)
                  : new Date(),
                amount: Math.round(Number(data?.amount) || 0),
                invoiceNumber: data?.invoiceNumber || "",
                invoiceDate: data?.invoiceDate
                  ? new Date(data.invoiceDate)
                  : new Date(),
                paymentMethod: data?.paymentMethod || "transfer",
                remarks: data?.remarks || undefined,
                checkNumber: data?.checkNumber || undefined,
                checkClearanceDate: data?.checkClearanceDate
                  ? new Date(data.checkClearanceDate)
                  : undefined,
                handlingFee: Math.round(Number(data?.handlingFee) || 0),
                retentionAmount: Math.round(Number(data?.retentionAmount) || 0),
                deductionAmount: Math.round(Number(data?.deductionAmount) || 0),
                isSettled: data?.isSettled ?? false,
              }))
            )
          );
        }

        if (newList.length) {
          await allOrThrow(
            orpcClient.accountsReceivableDetail.create(
              newList.map((data) => ({
                accountsReceivableId: id!,
                bankId: data?.bankId || "",
                paymentDate: data?.paymentDate
                  ? new Date(data.paymentDate)
                  : new Date(),
                amount: Math.round(Number(data?.amount) || 0),
                invoiceNumber: data?.invoiceNumber || "",
                invoiceDate: data?.invoiceDate
                  ? new Date(data.invoiceDate)
                  : new Date(),
                paymentMethod: data?.paymentMethod || "transfer",
                remarks: data?.remarks || undefined,
                checkNumber: data?.checkNumber || undefined,
                checkClearanceDate: data?.checkClearanceDate
                  ? new Date(data.checkClearanceDate)
                  : undefined,
                handlingFee: Math.round(Number(data?.handlingFee) || 0),
                retentionAmount: Math.round(Number(data?.retentionAmount) || 0),
                deductionAmount: Math.round(Number(data?.deductionAmount) || 0),
                isSettled: data?.isSettled ?? false,
              }))
            )
          );
        }

        navigate("/hrMainPage/accountReceivable");
      }
    } catch (error) {
      api.error({
        message: `Notification `,
        description: (
          <div>{(error as any)?.message || t("accountReceivable.updateFailed") || "更新失敗"}</div>
        ),
        placement: "topLeft",
        duration: 0,
      });
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [partners, banks, ar] = await Promise.all([
          orpcClient.partner.list({}),
          orpcClient.bank.list({}),
          single(orpcClient.accountsReceivable.read([{ id: id! }])),
        ]);
        setPartnerData(partners);
        setBankData(banks);

        if (ar) {
          setAccountReceivableData({
            name: ar.name || "",
            requestedAmount: ar.requestedAmount || 0,
            taxRate: ar.taxRate || 0,
            partnerId: ar.partnerId || "",
            remarks: ar.remarks || "",
          });
          const partner = (partners as any[]).find((p) => p.id === ar.partnerId);
          if (partner) {
            setTempPartnerInput(`${partner.name} (ID: ${partner.id})`);
          }
          const docRecords: any = ar.documentRecords || {};
          setPerformanceBond(
            docRecords.performanceBond || {
              checkNumber: "",
              retrievalDate: null,
              isRetrieved: false,
            }
          );
          setEngineeringInsurance(
            docRecords.engineeringInsurance || {
              startDate: null,
              expiryDate: null,
            }
          );
          setWarrantyBond(
            docRecords.warrantyBond || {
              checkNumber: "",
              retrievalDate: null,
              isRetrieved: false,
            }
          );

          const details = (ar as any).accountsReceivableDetail ?? [];
          const fetchedDetails = await allOrThrow(
            orpcClient.accountsReceivableDetail.read(
              details.map((d: any) => ({ id: d.id }))
            )
          );
          setReceivableList(fetchedDetails as any[]);
        }
      } catch (error) {
        console.error("Error loading account receivable:", error);
      }
    };

    if (id) fetchAll();
  }, [id]);

  return (
    <div className="min-h-screen p-8">
      {contextHolder}
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div className="py-3 text-4xl font-bold" style={{ color: "#6F6F6F" }}>
          {t("accountReceivable.editAccountReceivable") || "編輯應收帳款"}
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <IconCashBanknote size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountReceivable.totalAmountWithTax") || "總金額（含稅)"}
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDecimal(totalAmountWithTax)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <IconReceipt size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountReceivable.paidAmount") || "已收金額"}
                </p>
                <p className="text-lg font-semibold text-green-600">
                  {formatDecimal(paidAmount)}
                </p>
              </div>
            </div>
          </div>

          <div
            className={`rounded-lg p-4 shadow-sm ${unpaidAmount > 0 ? "border border-orange-200 bg-orange-50" : "border border-green-200 bg-green-50"}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${unpaidAmount > 0 ? "bg-orange-100" : "bg-green-100"}`}
              >
                <IconCashBanknote
                  size={20}
                  className={unpaidAmount > 0 ? "text-orange-600" : "text-green-600"}
                />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountReceivable.unpaidAmount") || "未收金額 (含稅)"}
                </p>
                <p
                  className={`text-lg font-semibold ${unpaidAmount > 0 ? "text-orange-600" : "text-green-600"}`}
                >
                  {formatDecimal(unpaidAmount)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                <IconPercentage size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountReceivable.totalHandlingFee") || "總手續費"}
                </p>
                <p className="text-lg font-semibold text-purple-600">
                  {formatDecimal(totalHandlingFee)}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                <IconCalculator size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  {t("accountReceivable.totalDeductionAmount") || "總扣款金額"}
                </p>
                <p className="text-lg font-semibold text-red-600">
                  {formatDecimal(totalDeductionAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("accountReceivable.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-6 gap-4 p-6">
            <div className="col-span-3 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountReceivable.accountReceivableName") ||
                  "應收帳款名稱"}＊
              </label>
              <Input
                value={name}
                onChange={(e) => handleChangeData(e.target.value, "name")}
                placeholder={
                  t("accountReceivable.enterName") || "請輸入名稱"
                }
                leftSection={<IconMail size={16} />}
              />
            </div>

            <div className="col-span-3 space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <IconUsers size={14} />
                {t("accountReceivable.partnerSupplier") || "合作方/供應商"}＊
              </label>
              <AutoComplete
                style={{ width: "100%" }}
                value={tempPartnerInput}
                placeholder={
                  t("accountReceivable.enterPartner") || "請輸入合作方名稱"
                }
                options={options}
                onFocus={() =>
                  setOptions(handleGetPartnerData(partnerData, ""))
                }
                onSearch={(text) =>
                  setOptions(handleGetPartnerData(partnerData, text))
                }
                onChange={(e) => setTempPartnerInput(e)}
                onSelect={(e) =>
                  handleChangePartnerData(e, partnerData, "partnerId")
                }
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountReceivable.requestedAmount") || "應收金額"}＊
              </label>
              <NumberInput
                value={requestedAmount}
                thousandSeparator=","
                onChange={(e) => handleChangeData(e, "requestedAmount")}
                leftSection={<IconCashBanknote size={16} />}
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountReceivable.taxRate") || "稅率(%)"}
              </label>
              <NumberInput
                value={taxRate}
                onChange={(e) => handleChangeData(e, "taxRate")}
                leftSection={<IconPercentage size={16} />}
              />
            </div>

            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountReceivable.taxAmount") || "稅額"}
              </label>
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                {formatDecimal(
                  (Number(requestedAmount) || 0) *
                    ((Number(taxRate) || 0) / 100)
                )}
              </div>
            </div>

            <div className="col-span-6 space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("accountReceivable.totalAmountWithTax") || "含稅總金額"}
              </label>
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-900">
                {formatDecimal(
                  (Number(requestedAmount) || 0) +
                    (Number(requestedAmount) || 0) *
                      ((Number(taxRate) || 0) / 100)
                )}
              </div>
            </div>

            <div className="col-span-6 space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <IconMessage size={14} />
                {t("accountReceivable.remarks") || "備註"}
              </label>
              <Textarea
                placeholder="請輸入備註事項..."
                value={remarks}
                onChange={(e) => handleChangeData(e.target.value, "remarks")}
                minRows={2}
                autosize
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div
            className="flex cursor-pointer items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4 hover:bg-gray-100"
            onClick={() => setIsDocumentsOpen(!isDocumentsOpen)}
          >
            <div className="flex items-center gap-2">
              <IconFolderOpen size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("accountReceivable.documentRecords") || "文件紀錄"}
              </h3>
              <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-500">
                {
                  [performanceBond.isRetrieved, warrantyBond.isRetrieved].filter(
                    Boolean
                  ).length
                }{" "}
                / 2 {t("accountReceivable.retrieved") || "已取回"}
              </span>
            </div>
            <IconChevronDown
              size={16}
              className={`text-gray-400 transition-transform duration-200 ${isDocumentsOpen ? "rotate-180" : ""}`}
            />
          </div>

          {isDocumentsOpen && (
            <div>
              <div className="border-b border-gray-100 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <IconShield size={16} className="text-blue-600" />
                  <h4 className="text-sm font-medium text-gray-700">
                    {t("accountReceivable.performanceGuarantee") || "履約保證"}
                  </h4>
                  {performanceBond.isRetrieved && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                      {t("accountReceivable.retrieved") || "已取回"}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconHash size={13} />
                      {t("accountReceivable.voucherNumber") || "票號"}
                    </label>
                    <Input
                      value={performanceBond.checkNumber}
                      onChange={(e) =>
                        setPerformanceBond((prev) => ({
                          ...prev,
                          checkNumber: e.target.value,
                        }))
                      }
                      placeholder={
                        t("accountReceivable.enterVoucherNumber") || "請輸入票號"
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountReceivable.retrievalDate") || "取回日期"}
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        performanceBond.retrievalDate
                          ? dayjs(performanceBond.retrievalDate)
                          : null
                      }
                      onChange={(e) =>
                        setPerformanceBond((prev) => ({
                          ...prev,
                          retrievalDate: e ? e.format("YYYY-MM-DD") : null,
                        }))
                      }
                    />
                  </div>
                  <div className="flex items-end pb-1 lg:col-span-2">
                    <Checkbox
                      checked={performanceBond.isRetrieved}
                      onChange={(e) =>
                        setPerformanceBond((prev) => ({
                          ...prev,
                          isRetrieved: e.currentTarget.checked,
                        }))
                      }
                      label={
                        <span className="flex items-center gap-1.5">
                          <IconShieldCheck size={14} className="text-green-600" />
                          {t("accountReceivable.retrievedGuarantee") ||
                            "已取回履約保證"}
                        </span>
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <IconShieldCheck size={16} className="text-blue-600" />
                  <h4 className="text-sm font-medium text-gray-700">
                    {t("accountReceivable.constructionInsurance") || "工程保險"}
                  </h4>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountReceivable.startDate") || "起始日期"}
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        engineeringInsurance.startDate
                          ? dayjs(engineeringInsurance.startDate)
                          : null
                      }
                      onChange={(e) =>
                        setEngineeringInsurance((prev) => ({
                          ...prev,
                          startDate: e ? e.format("YYYY-MM-DD") : null,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountReceivable.expirationDate") || "到期日期"}
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        engineeringInsurance.expiryDate
                          ? dayjs(engineeringInsurance.expiryDate)
                          : null
                      }
                      onChange={(e) =>
                        setEngineeringInsurance((prev) => ({
                          ...prev,
                          expiryDate: e ? e.format("YYYY-MM-DD") : null,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4 flex items-center gap-2">
                  <IconTicket size={16} className="text-blue-600" />
                  <h4 className="text-sm font-medium text-gray-700">
                    {t("accountReceivable.warrantyBond") || "保固本票"}
                  </h4>
                  {warrantyBond.isRetrieved && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                      {t("accountReceivable.retrieved") || "已取回"}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconHash size={13} />
                      {t("accountReceivable.voucherNumber") || "票號"}
                    </label>
                    <Input
                      value={warrantyBond.checkNumber}
                      onChange={(e) =>
                        setWarrantyBond((prev) => ({
                          ...prev,
                          checkNumber: e.target.value,
                        }))
                      }
                      placeholder={
                        t("accountReceivable.enterVoucherNumber") || "請輸入票號"
                      }
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountReceivable.retrievalDate") || "取回日期"}
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        warrantyBond.retrievalDate
                          ? dayjs(warrantyBond.retrievalDate)
                          : null
                      }
                      onChange={(e) =>
                        setWarrantyBond((prev) => ({
                          ...prev,
                          retrievalDate: e ? e.format("YYYY-MM-DD") : null,
                        }))
                      }
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <Checkbox
                      checked={warrantyBond.isRetrieved}
                      onChange={(e) =>
                        setWarrantyBond((prev) => ({
                          ...prev,
                          isRetrieved: e.currentTarget.checked,
                        }))
                      }
                      label={
                        <span className="flex items-center gap-1.5">
                          <IconShieldCheck size={14} className="text-green-600" />
                          {t("accountReceivable.retrievedWarrantyBond") ||
                            "已取回保固本票"}
                        </span>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <IconReceipt size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">
                {t("accountReceivable.collectionRecord") || "收款紀錄"}
              </h3>
              <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-500">
                {receivableList.length}{" "}
                {t("accountReceivable.records") || "筆"}
              </span>
            </div>
            <Button
              variant="default"
              radius="md"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={() =>
                setReceivableList((prev) => [
                  ...prev,
                  {
                    paymentDate: null,
                    amount: "",
                    bankId: "",
                    paymentMethod: "transfer",
                    invoiceNumber: "",
                    invoiceDate: "",
                    checkNumber: "",
                    checkClearanceDate: null,
                    handlingFee: 0,
                    retentionAmount: 0,
                    deductionAmount: 0,
                    isSettled: false,
                    remarks: "",
                    key: uuidv4(),
                  },
                ])
              }
            >
              {t("accountReceivable.addCollectionRecord") || "增加收款紀錄"}
            </Button>
          </div>

          <div className="divide-y divide-gray-100">
            {receivableList.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <IconReceipt size={48} className="mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-400">
                  {t("accountReceivable.noCollectionData") || "無收款資料"}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {t("accountReceivable.clickToAddRecord") ||
                    "點擊上方按鈕新增收款紀錄"}
                </p>
              </div>
            )}

            {receivableList.map((record, index) => (
              <div key={record.id || record.key} className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-600">
                    {t("accountReceivable.collectionRecordLabel") ||
                      "收款紀錄 #"}
                    {index + 1}
                  </span>
                  <Button
                    variant="subtle"
                    color="red"
                    size="sm"
                    radius="md"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => {
                      if (record?.id) {
                        setDeleteReceivableList((prev) => [...prev, record.id]);
                      }
                      setReceivableList((prev) =>
                        prev.filter((r) => {
                          if (record?.id) return r.id !== record?.id;
                          return r?.key !== record?.key;
                        })
                      );
                    }}
                  >
                    {t("accountReceivable.delete") || "刪除"}
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountReceivable.collectionDate") || "收款日期"}＊
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        record?.paymentDate ? dayjs(record?.paymentDate) : null
                      }
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e ? e.format("YYYY-MM-DD") : null,
                          "paymentDate"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCashBanknote size={13} />
                      {t("accountReceivable.collectionAmount") || "收款金額"}＊
                    </label>
                    <NumberInput
                      value={record?.amount}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e,
                          "amount"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconBuilding size={13} />
                      {t("accountReceivable.bank") || "銀行"}＊
                    </label>
                    <AutoComplete
                      style={{ width: "100%" }}
                      value={
                        getBankById(record?.bankId)
                          ? `${getBankById(record?.bankId).name} (ID: ${record?.bankId})`
                          : record?.bankId || ""
                      }
                      options={bankData?.map((b) => ({
                        label: b?.name + ` (ID: ${b?.id})`,
                        value: b?.name + ` (ID: ${b?.id})`,
                      }))}
                      onChange={(value) => {
                        let match = value.match(/ID:\s*([a-zA-Z0-9-]+)/);
                        let idNumber = match ? match[1] : null;
                        if (idNumber) {
                          handleChangeReceivableDetail(
                            record?.id || record.key,
                            idNumber,
                            "bankId"
                          );
                        }
                      }}
                      placeholder={t("bank.enterBankName") || "請輸入銀行名稱"}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCashBanknote size={13} />
                      {t("accountReceivable.paymentMethod") || "付款方式"}＊
                    </label>
                    <Select
                      value={record?.paymentMethod || "transfer"}
                      data={[
                        {
                          value: "transfer",
                          label: t("accountReceivable.transfer") || "匯款",
                        },
                        {
                          value: "check",
                          label: t("accountReceivable.check") || "支票",
                        },
                        {
                          value: "cash",
                          label: t("accountReceivable.cash") || "現金",
                        },
                        {
                          value: "credit",
                          label: t("accountReceivable.credit") || "信用卡",
                        },
                      ]}
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e,
                          "paymentMethod"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconHash size={13} />
                      {t("accountReceivable.invoiceNumber") || "發票號碼"}＊
                    </label>
                    <Input
                      value={record?.invoiceNumber}
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e.target.value,
                          "invoiceNumber"
                        )
                      }
                      placeholder="INV-XXXX-XXX"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountReceivable.invoiceDate") || "發票日期"}＊
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        record?.invoiceDate ? dayjs(record?.invoiceDate) : null
                      }
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e ? e.format("YYYY-MM-DD") : null,
                          "invoiceDate"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconHash size={13} />
                      {t("accountReceivable.checkNumber") || "支票號碼"}
                    </label>
                    <Input
                      value={record?.checkNumber || ""}
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e.target.value,
                          "checkNumber"
                        )
                      }
                      placeholder="CHK-XXXXX"
                      disabled={record?.paymentMethod !== "check"}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalendar size={13} />
                      {t("accountReceivable.checkClearanceDate") || "兌現日期"}
                    </label>
                    <DatePicker
                      style={{ width: "100%" }}
                      value={
                        record?.checkClearanceDate
                          ? dayjs(record?.checkClearanceDate)
                          : null
                      }
                      disabled={record?.paymentMethod !== "check"}
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e ? e.format("YYYY-MM-DD") : null,
                          "checkClearanceDate"
                        )
                      }
                    />
                  </div>

                  <div className="col-span-full my-1 border-t border-gray-100" />

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconPercentage size={13} />
                      {t("accountReceivable.handlingFee") || "手續費"}
                    </label>
                    <NumberInput
                      value={record?.handlingFee ?? 0}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e,
                          "handlingFee"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconWallet size={13} />
                      {t("accountReceivable.retentionAmount") || "保留款"}
                    </label>
                    <NumberInput
                      value={record?.retentionAmount ?? 0}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e,
                          "retentionAmount"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconCalculator size={13} />
                      {t("accountReceivable.deductionAmount") || "扣款金額"}
                    </label>
                    <NumberInput
                      value={record?.deductionAmount ?? 0}
                      thousandSeparator=","
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e,
                          "deductionAmount"
                        )
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs text-gray-500">
                      {t("accountReceivable.netAmount") || "實收金額"}
                    </label>
                    <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3">
                      <span
                        className={`font-semibold ${record?.isSettled ? "text-green-600" : "text-gray-900"}`}
                      >
                        {formatDecimal(
                          Number(record?.amount || 0) -
                            (record?.handlingFee || 0) -
                            (record?.retentionAmount || 0) -
                            (record?.deductionAmount || 0)
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-end pb-1">
                    <Checkbox
                      checked={record?.isSettled || false}
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e.currentTarget.checked,
                          "isSettled"
                        )
                      }
                      label={t("accountReceivable.isSettled") || "已結清"}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconMessage size={13} />
                      {t("accountReceivable.remarks") || "備註"}
                    </label>
                    <Textarea
                      value={record?.remarks}
                      onChange={(e) =>
                        handleChangeReceivableDetail(
                          record?.id || record.key,
                          e.target.value,
                          "remarks"
                        )
                      }
                      placeholder={
                        t("accountReceivable.enterRemarks") || "備註"
                      }
                      minRows={1}
                      autosize
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleCancelButton()}
          >
            {t("accountReceivable.cancel") || "取消"}
          </Button>

          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleSubmit()}
          >
            {t("hrMain.save") || "儲存"}
          </Button>
        </div>
      </div>
    </div>
  );
}
export default EditAccountReceivablePage;
